import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Badge,
  Table,
  Form,
  InputGroup,
  ProgressBar,
  Modal,
  Pagination,
  Spinner,
} from "@themesberg/react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChartPie,
  faBuilding,
  faUsers,
  faCalendarAlt,
  faFilter,
  faSearch,
  faEye,
  faCheckCircle,
  faExclamationTriangle,
  faClock,
  faShieldAlt,
  faCoffee,
  faUtensils,
  faUserClock,
  faDownload,
  faTimes,
  faSync,
  faTasks,
} from "@fortawesome/free-solid-svg-icons";
import {
  formatDuration,
  formatDurationHuman,
  formatBreakTimeMinutes,
  formatDateStr,
  formatTimeStr,
  getIsoDateStr,
  isDateInRange,
  parseDurationToSeconds,
  parseScreenActiveTimeToSeconds,
  parseBreakTimeToSeconds,
  parseDateSafe,
} from "./ActivityTrackerContext";
import { getDestDetails } from "../../Service/AllActivityTrackerAPI";
import { TaskManagementProvider } from "./TaskManagementContext";
import ManagerTaskManagement from "./ManagerTaskManagement";
import "./ActivityTracker.css";

// Storage keys
const STORAGE_STATE_KEY = "PETROPULSE_EMPLOYEE_TRACKER_STATE";

// Helper to format ISO date string to DD Mon YYYY
const formatIsoToDisplay = (isoStr) => {
  if (!isoStr) return "";
  const parts = isoStr.split("-");
  if (parts.length === 3) {
    const d = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
    return d.toLocaleDateString("en-US", { day: "2-digit", month: "short", year: "numeric" });
  }
  return isoStr;
};


// Helper to transform server desktop record from /User/getdestDetails into manager workforce schema
const transformDeskRecordToWorkforce = (item, index = 0, currentTimestamp = Date.now()) => {
  if (!item) return null;

  const rawLogin =
    item.desklogintime || item.loginTime || item.login_time || item.desk_login_time;
  const rawLogout =
    item.desklogouttime || item.logoutTime || item.logout_time || item.desk_logout_time;
  const rawDateTime =
    item.dateandtime || item.date_and_time || item.createdAt || item.createdDate;

  const offsetMatch = rawLogin ? String(rawLogin).match(/([+-]\d{2}:\d{2}|Z)$/) : null;
  const fallbackOffset = offsetMatch ? offsetMatch[1] : null;

  const loginDate = parseDateSafe(rawLogin) || parseDateSafe(rawDateTime);
  const logoutDate = parseDateSafe(rawLogout, fallbackOffset);
  const hasLogout =
    rawLogout &&
    rawLogout !== "0" &&
    String(rawLogout).trim() !== "" &&
    String(rawLogout).trim() !== "0";

  const baseTs = loginDate ? loginDate.getTime() : Date.now();
  const dateStr = loginDate ? formatDateStr(baseTs) : formatDateStr(Date.now());
  const isoDate = loginDate ? getIsoDateStr(baseTs) : getIsoDateStr(Date.now());
  const loginTimeFormatted = loginDate ? formatTimeStr(baseTs) : (rawLogin && rawLogin !== "0" && String(rawLogin).trim() !== "" ? String(rawLogin) : "--");
  const logoutTimeFormatted = logoutDate ? formatTimeStr(logoutDate.getTime()) : (hasLogout ? String(rawLogout) : "--");

  // Parse durations: screen lock time, idle time, break time
  const rawScreenLock =
    item.desktotalScreenlocktime !== undefined && item.desktotalScreenlocktime !== null
      ? item.desktotalScreenlocktime
      : item.desktotalscreenlocktime !== undefined && item.desktotalscreenlocktime !== null
      ? item.desktotalscreenlocktime
      : item.screenlocktime !== undefined && item.screenlocktime !== null
      ? item.screenlocktime
      : item.screenLockTime !== undefined && item.screenLockTime !== null
      ? item.screenLockTime
      : "0";

  const rawIdle =
    item.desktotalidletime !== undefined && item.desktotalidletime !== null
      ? item.desktotalidletime
      : item.desktotal_idletime !== undefined && item.desktotal_idletime !== null
      ? item.desktotal_idletime
      : item.idletime !== undefined && item.idletime !== null
      ? item.idletime
      : item.idleTime !== undefined && item.idleTime !== null
      ? item.idleTime
      : "0";

  const rawBreak =
    item.breaktime !== undefined && item.breaktime !== null
      ? item.breaktime
      : item.break_time !== undefined && item.break_time !== null
      ? item.break_time
      : item.breakTime !== undefined && item.breakTime !== null
      ? item.breakTime
      : "0";

  const screenLockSec = parseDurationToSeconds(rawScreenLock);
  const idleSec = parseDurationToSeconds(rawIdle);
  const breakSec = parseBreakTimeToSeconds(rawBreak);
  const inactiveSec = screenLockSec + idleSec;

  // Direct screen active time from API / desktop companion agent record
  const rawScreenActive =
    item.screenactivetime !== undefined && item.screenactivetime !== null
      ? item.screenactivetime
      : item.screenActiveTime !== undefined && item.screenActiveTime !== null
      ? item.screenActiveTime
      : item.screen_active_time !== undefined && item.screen_active_time !== null
      ? item.screen_active_time
      : item.desktotalscreenactivetime !== undefined && item.desktotalscreenactivetime !== null
      ? item.desktotalscreenactivetime
      : item.desktotalScreenactivetime !== undefined && item.desktotalScreenactivetime !== null
      ? item.desktotalScreenactivetime
      : item.desktotalactiveworktime !== undefined && item.desktotalactiveworktime !== null
      ? item.desktotalactiveworktime
      : null;

  const parsedScreenActiveSec =
    rawScreenActive !== null && rawScreenActive !== ""
      ? parseScreenActiveTimeToSeconds(rawScreenActive)
      : 0;

  const todayIso = getIsoDateStr(currentTimestamp);

  let computedLogoutFormatted = logoutTimeFormatted;
  let totalElapsed = 0;

  if (loginDate && logoutDate) {
    totalElapsed = Math.max(0, Math.floor((logoutDate.getTime() - loginDate.getTime()) / 1000));
  } else if (loginDate) {
    if (isoDate !== todayIso) {
      computedLogoutFormatted = "--";
      totalElapsed = Math.max(0, parsedScreenActiveSec + inactiveSec + breakSec);
    } else {
      totalElapsed = Math.max(0, Math.floor((currentTimestamp - loginDate.getTime()) / 1000));
      computedLogoutFormatted = "--";
    }
  }

  // Active work time: STRICTLY from response screenactivetime only
  const activeSec = parsedScreenActiveSec || 0;

  // Total shift duration: strictly elapsed time from login until logout (or now if active)
  let totalShiftHrs = totalElapsed > 0 ? totalElapsed : (activeSec + inactiveSec + breakSec);

  // Determine Work Status purely based on login and logout response:
  // - "Active": login exists and logout is "0" (or not set)
  // - "Shift Completed": logout exists
  // - "Not Started Yet": no login or login is "0"
  const hasLogin =
    rawLogin &&
    rawLogin !== "0" &&
    rawLogin !== 0 &&
    String(rawLogin).trim() !== "" &&
    String(rawLogin).trim() !== "0";

  let status = "Not Started Yet";
  let currentBreakType = null;
  if (hasLogout) {
    status = "Shift Completed";
  } else if (hasLogin) {
    if (isoDate === todayIso) {
      status = "Active";
    } else {
      status = "Shift Completed";
    }
  } else {
    status = "Not Started Yet";
  }

  // Employee derivation directly from real server record properties
  const username = item.deskusername || (item.username ? String(item.username) : `User-${index + 1}`);

  let formattedName =
    item.fullname ||
    item.fullName ||
    item.full_name ||
    item.empname ||
    item.emp_name ||
    item.name ||
    [item.firstName, item.lastName].filter(Boolean).join(" ");
  if (!formattedName) {
    formattedName = username
      .replace(/@.*$/, "") // strip email domain if present
      .split(/[._\-\s]+/)
      .filter(Boolean)
      .map((p) => p.charAt(0).toUpperCase() + p.slice(1).toLowerCase())
      .join(" ");
  }

  const empId =
    item.pk_user_id ||
    item.pkUserId ||
    item.fkuserid ||
    item.fkUserId ||
    item.fk_user_id ||
    item.fkempid ||
    item.emp_id ||
    item.empId ||
    item.userId ||
    item.deskusername ||
    item.username ||
    (item.id ? `ID-${item.id}` : `USR-${index + 1}`);

  const dept =
    item.department ||
    item.dept ||
    item.dept_name ||
    item.departmentName ||
    item.userDepartment ||
    "General";

  const role =
    item.designation ||
    item.role ||
    item.user_role ||
    item.userRole ||
    item.userDesignation ||
    "Staff";

  // Score calculation
  let score = 95;
  if (totalShiftHrs > 0) {
    score = Math.min(100, Math.max(65, Math.round((activeSec / totalShiftHrs) * 100)));
  }

  const breaks = breakSec > 0 ? [
    {
      id: `BRK-${item.id || index}`,
      type: "Break",
      reason: "Short Break",
      startTime: "--",
      endTime: "--",
      durationSeconds: breakSec,
      durationFormatted: formatBreakTimeMinutes(breakSec),
      status: "Completed",
    }
  ] : [];

  return {
    id: String(empId),
    name: formattedName,
    dept,
    role,
    date: dateStr,
    isoDate,
    status,
    currentBreakType,
    attendance: "Present",
    loginTime: loginTimeFormatted,
    logoutTime: computedLogoutFormatted,
    activeHrs: activeSec,
    screenActiveTime: activeSec,
    inactiveHrs: inactiveSec,
    breakHrs: breakSec,
    screenLockHrs: screenLockSec,
    idleHrs: idleSec,
    totalShiftHrs,
    score,
    breaks,
    hasLogout,
    isDesktopServerRecord: true,
    deskusername: username,
    rawServerId: item.id,
    desktotalScreenlocktime: String(rawScreenLock || screenLockSec || "0"),
    desktotalidletime: String(rawIdle || idleSec || "0"),
    screenactivetime: String(rawScreenActive !== null && rawScreenActive !== undefined ? rawScreenActive : activeSec),
    breaktime: String(rawBreak || breakSec || "0"),
  };
};

export const ManagerAnalytics = () => {
  // Screen View Switcher: Default to 'ATTENDANCE' (Workforce Attendance)
  const [managerScreenView, setManagerScreenView] = useState("ATTENDANCE");

  // Server live session
  const [liveSession, setLiveSession] = useState(null);

  // Server Desktop details records state
  const [serverDeskRecords, setServerDeskRecords] = useState([]);
  const [isServerLoading, setIsServerLoading] = useState(false);
  const [, setServerError] = useState(null);
  const [lastSyncTime, setLastSyncTime] = useState(null);

  // Filter states: Default to 'ALL' to show all live records from server
  const [dateFilter, setDateFilter] = useState("ALL"); // 'ALL' | 'TODAY' | 'YESTERDAY' | 'LAST_7_DAYS' | 'LAST_30_DAYS' | 'THIS_MONTH' | 'CUSTOM' | 'CUSTOM_RANGE'
  const [customDate, setCustomDate] = useState(() => getIsoDateStr(Date.now()));
  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 6);
    return getIsoDateStr(d);
  });
  const [endDate, setEndDate] = useState(() => getIsoDateStr(Date.now()));
  const [selectedDept, setSelectedDept] = useState("ALL");
  const [statusFilter, setStatusFilter] = useState("ALL"); // 'ALL' | 'Present' | 'Working' | 'On Break' | 'Screen Inactive' | 'Completed' | 'Leave' | 'Active Work' | 'Break Time'
  const [activeCardFilter, setActiveCardFilter] = useState("ALL");
  const [searchEmployee, setSearchEmployee] = useState("");

  // Table section ref for smooth scrolling on card click
  const tableSectionRef = useRef(null);

  // Modal drilldown state
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [showStaffModal, setShowStaffModal] = useState(false);

  // Fetch Desktop details from server API (/User/getdestDetails?role=Employee&fkUserId=...)
  const lastDeskJsonRef = useRef("");
  const fetchDeskDetails = useCallback(async () => {
    try {
      setIsServerLoading(true);
      setServerError(null);
      const role = sessionStorage.getItem("role") || "Employee";
      const fkUserId = sessionStorage.getItem("pkUserId") || "";
      const res = await getDestDetails(role, fkUserId);
      const rawRecords = Array.isArray(res?.data)
        ? res.data
        : Array.isArray(res?.data?.data)
        ? res.data.data
        : Array.isArray(res?.data?.list)
        ? res.data.list
        : Array.isArray(res?.data?.result)
        ? res.data.result
        : null;

      if (rawRecords && rawRecords.length > 0) {
        const jsonStr = JSON.stringify(rawRecords);
        if (jsonStr !== lastDeskJsonRef.current) {
          lastDeskJsonRef.current = jsonStr;
          setServerDeskRecords(rawRecords);
          setLastSyncTime(new Date());
        }
      } else {
        // No data available in server response: clear records with no dummy/fallback data
        lastDeskJsonRef.current = "[]";
        setServerDeskRecords([]);
        setLastSyncTime(new Date());
      }
    } catch (err) {
      console.error("Error fetching desktop details:", err);
      setServerError(err.message || "Failed to load desktop tracking records");
      lastDeskJsonRef.current = "[]";
      setServerDeskRecords([]);
    } finally {
      setIsServerLoading(false);
    }
  }, []);

  // Auto-fetch desktop records on mount and periodic refresh every 45s
  useEffect(() => {
    fetchDeskDetails();
    const refreshTimer = setInterval(() => {
      fetchDeskDetails();
    }, 45000);
    return () => clearInterval(refreshTimer);
  }, [fetchDeskDetails]);

  // Live polling of logged-in user's active session from localStorage (guards against duplicate objects)
  const lastLiveSessionStrRef = useRef("");
  useEffect(() => {
    const syncLiveUserSession = () => {
      try {
        const username = sessionStorage.getItem("userName");
        const cleanUser = username
          ? String(username).trim().toLowerCase().replace(/[^a-zA-Z0-9_.-]/g, "_")
          : null;
        const userScopedKey = cleanUser ? `${STORAGE_STATE_KEY}_${cleanUser}` : STORAGE_STATE_KEY;
        const saved = localStorage.getItem(userScopedKey) || localStorage.getItem(STORAGE_STATE_KEY);

        if (saved) {
          if (saved !== lastLiveSessionStrRef.current) {
            lastLiveSessionStrRef.current = saved;
            const parsed = JSON.parse(saved);
            setLiveSession(parsed);
          }
        } else if (lastLiveSessionStrRef.current !== "") {
          lastLiveSessionStrRef.current = "";
          setLiveSession(null);
        }
      } catch (err) {}
    };

    syncLiveUserSession();
    const interval = setInterval(syncLiveUserSession, 3000);
    return () => clearInterval(interval);
  }, []);

  // Live ticking clock to dynamically advance Active Work Time for working staff every second
  const [currentTickerTime, setCurrentTickerTime] = useState(() => Date.now());
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTickerTime(Date.now());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // Today's formatted display string
  const todayDisplay = useMemo(() => formatDateStr(Date.now()), []);

  // Yesterday's formatted display string
  const yesterdayDisplay = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() - 1);
    return formatDateStr(d.getTime());
  }, []);

  // Compute records purely from live server desktop records (zero fallback dummy data)
  const allMergedRecords = useMemo(() => {
    // 1. Transform desktop server records from API using dynamic currentTickerTime
    const transformedServerRecords = (serverDeskRecords || [])
      .map((item, idx) => transformDeskRecordToWorkforce(item, idx, currentTickerTime))
      .filter(Boolean);

    // If there is no data available from the server API, do NOT show any fallback dummy data!
    if (transformedServerRecords.length === 0) {
      return [];
    }

    let records = [...transformedServerRecords];

    // 2. Synchronize each active employee's live work / break status from their real-time session state
    const sessionUser = (sessionStorage.getItem("userName") || "").trim().toLowerCase();
    const sessionCleanUser = sessionUser.replace(/[^a-z0-9]/g, "");
    const sessionEmpId = sessionStorage.getItem("pkUserId") || sessionStorage.getItem("userId") || "";

    records = records.map((existing) => {
      if (existing.hasLogout) return existing;

      const rUser = existing.deskusername || existing.username || "";
      const cleanUser = String(rUser).trim().toLowerCase().replace(/[^a-zA-Z0-9_.-]/g, "_");
      const cleanNoSpecial = String(rUser).trim().toLowerCase().replace(/[^a-z0-9]/g, "");

      let empSession = null;
      if (cleanUser) {
        const key = `${STORAGE_STATE_KEY}_${cleanUser}`;
        const saved = localStorage.getItem(key);
        if (saved) {
          try { empSession = JSON.parse(saved); } catch (e) {}
        }
      }
      if (!empSession && cleanNoSpecial) {
        for (let i = 0; i < localStorage.length; i++) {
          const k = localStorage.key(i);
          if (k && k.startsWith(STORAGE_STATE_KEY) && k.toLowerCase().replace(/[^a-z0-9]/g, "").includes(cleanNoSpecial)) {
            try {
              const saved = localStorage.getItem(k);
              if (saved) { empSession = JSON.parse(saved); break; }
            } catch (e) {}
          }
        }
      }
      if (!empSession && liveSession && sessionUser && (cleanNoSpecial === sessionCleanUser || existing.id === sessionEmpId)) {
        empSession = liveSession;
      }

      if (empSession && empSession.isShiftActive) {
        const liveStatus = (empSession.inactiveReason === "SHIFT_ENDED" || existing.hasLogout)
          ? "Shift Completed"
          : "Active";

        const activeSeconds = Math.max(0, Number(empSession.activeSeconds) || existing.activeHrs || 0);
        const breakSeconds = Math.max(0, Number(empSession.breakSeconds) || existing.breakHrs || 0);
        const inactiveSeconds = Math.max(0, Number(empSession.inactiveSeconds) || existing.inactiveHrs || 0);
        const screenLockHrs = empSession.screenLockSeconds || existing.screenLockHrs || 0;
        const total = activeSeconds + breakSeconds + inactiveSeconds;

        return {
          ...existing,
          status: liveStatus,
          currentBreakType: null,
          activeHrs: activeSeconds,
          screenActiveTime: activeSeconds,
          breakHrs: breakSeconds,
          inactiveHrs: inactiveSeconds,
          screenLockHrs: screenLockHrs,
          idleHrs: inactiveSeconds,
          totalShiftHrs: existing.totalShiftHrs || total,
          loginTime: empSession.sessionStartTime
            ? formatTimeStr(empSession.sessionStartTime)
            : existing.loginTime,
          logoutTime: liveStatus === "Shift Completed" ? existing.logoutTime : "-- (Active)",
        };
      }

      return existing;
    });

    return records;
  }, [serverDeskRecords, liveSession, currentTickerTime]);

  // Available departments derived dynamically from live records
  const availableDepartments = useMemo(() => {
    const set = new Set();
    (allMergedRecords || []).forEach((r) => {
      if (r.dept) set.add(r.dept);
    });
    return Array.from(set).sort();
  }, [allMergedRecords]);

  // Live staff detail for drilldown modal resolving against dynamic allMergedRecords
  const activeStaffDetail = useMemo(() => {
    if (!selectedStaff) return null;
    return (
      allMergedRecords.find(
        (r) =>
          r.id === selectedStaff.id &&
          r.date === selectedStaff.date &&
          (selectedStaff.rawServerId ? r.rawServerId === selectedStaff.rawServerId : true)
      ) || selectedStaff
    );
  }, [allMergedRecords, selectedStaff]);

  // Base records filtered by Date and Department (used for stable top summary counts)
  const dateAndDeptRecords = useMemo(() => {
    return allMergedRecords.filter((staff) => {
      // 1. Date / Duration Filter
      const matchesDate = isDateInRange(
        staff.isoDate || staff.date,
        dateFilter,
        customDate,
        startDate,
        endDate
      );

      // 2. Department Filter
      const matchesDept = selectedDept === "ALL" || staff.dept === selectedDept;

      return matchesDate && matchesDept;
    });
  }, [allMergedRecords, dateFilter, customDate, startDate, endDate, selectedDept]);

  // Calculate human-friendly duration label for badges and CSV
  const durationLabel = useMemo(() => {
    if (dateFilter === "TODAY") return `Today (${todayDisplay})`;
    if (dateFilter === "YESTERDAY") return `Yesterday (${yesterdayDisplay})`;
    if (dateFilter === "LAST_7_DAYS") {
      const d = new Date();
      d.setDate(d.getDate() - 6);
      return `Last 7 Days (${formatDateStr(d.getTime())} – ${todayDisplay})`;
    }
    if (dateFilter === "LAST_30_DAYS") {
      const d = new Date();
      d.setDate(d.getDate() - 29);
      return `Last 30 Days (${formatDateStr(d.getTime())} – ${todayDisplay})`;
    }
    if (dateFilter === "THIS_MONTH") {
      const d = new Date();
      d.setDate(1);
      return `This Month (${formatDateStr(d.getTime())} – ${todayDisplay})`;
    }
    if (dateFilter === "CUSTOM") {
      return `Date: ${formatIsoToDisplay(customDate)}`;
    }
    if (dateFilter === "CUSTOM_RANGE") {
      return `Duration: ${formatIsoToDisplay(startDate)} – ${formatIsoToDisplay(endDate)}`;
    }
    return "All Available Dates";
  }, [dateFilter, todayDisplay, yesterdayDisplay, customDate, startDate, endDate]);

  // Dynamic Metrics Summary for the selected date and department (stays stable across card clicks)
  const summary = useMemo(() => {
    const totalCount = dateAndDeptRecords.length;
    const activeCount = dateAndDeptRecords.filter((r) => r.status === "Active" || r.status === "Working").length;
    const completedCount = dateAndDeptRecords.filter((r) => r.status === "Shift Completed" || r.status === "Completed").length;
    const notStartedCount = dateAndDeptRecords.filter((r) => r.status === "Not Started Yet" || r.status === "Not Started").length;
    const workingCount = activeCount;
    const breakCount = dateAndDeptRecords.filter((r) => r.status === "On Break").length;
    const inactiveCount = dateAndDeptRecords.filter((r) => r.status === "Screen Inactive").length;
    const leaveCount = dateAndDeptRecords.filter((r) => r.status === "Leave").length;
    const presentCount = totalCount - notStartedCount - leaveCount;

    const totalActiveSec = dateAndDeptRecords.reduce((acc, curr) => acc + (curr.activeHrs || 0), 0);
    const totalBreakSec = dateAndDeptRecords.reduce((acc, curr) => acc + (curr.breakHrs || 0), 0);
    const avgActiveSec = presentCount > 0 ? Math.floor(totalActiveSec / presentCount) : 0;
    const avgBreakSec = presentCount > 0 ? Math.floor(totalBreakSec / presentCount) : 0;

    return {
      totalCount,
      presentCount: Math.max(0, presentCount),
      workingCount,
      activeCount,
      notStartedCount,
      breakCount,
      inactiveCount,
      completedCount,
      leaveCount,
      avgActiveStr: formatDurationHuman(avgActiveSec),
      avgBreakStr: formatBreakTimeMinutes(avgBreakSec),
    };
  }, [dateAndDeptRecords]);

  // Filter records based on Date, Department, Status (or Active Card Filter), and Search
  const filteredRecords = useMemo(() => {
    let result = dateAndDeptRecords.filter((staff) => {
      // 1. Status Filter
      let matchesStatus = true;
      if (statusFilter === "Active" || statusFilter === "Working") matchesStatus = staff.status === "Active" || staff.status === "Working";
      else if (statusFilter === "Shift Completed" || statusFilter === "Completed") matchesStatus = staff.status === "Shift Completed" || staff.status === "Completed";
      else if (statusFilter === "Not Started Yet" || statusFilter === "Not Started") matchesStatus = staff.status === "Not Started Yet" || staff.status === "Not Started";
      else if (statusFilter === "On Break") matchesStatus = staff.status === "On Break";
      else if (statusFilter === "Screen Inactive") matchesStatus = staff.status === "Screen Inactive";
      else if (statusFilter === "Leave") matchesStatus = staff.status === "Leave";
      else if (statusFilter === "Present") matchesStatus = staff.status !== "Not Started Yet" && staff.status !== "Not Started" && staff.status !== "Leave";
      else if (statusFilter === "Active Work") matchesStatus = staff.activeHrs > 0;
      else if (statusFilter === "Break Time") matchesStatus = staff.breakHrs > 0 || (staff.breaks && staff.breaks.length > 0);

      // 2. Search Filter
      const matchesSearch =
        staff.name.toLowerCase().includes(searchEmployee.toLowerCase()) ||
        staff.id.toLowerCase().includes(searchEmployee.toLowerCase()) ||
        staff.role.toLowerCase().includes(searchEmployee.toLowerCase()) ||
        staff.dept.toLowerCase().includes(searchEmployee.toLowerCase());

      return matchesStatus && matchesSearch;
    });

    // Smart Sorting if Active Work or Break Time is selected
    if (statusFilter === "Active Work") {
      result = [...result].sort((a, b) => (b.activeHrs || 0) - (a.activeHrs || 0));
    } else if (statusFilter === "Break Time") {
      result = [...result].sort((a, b) => (b.breakHrs || 0) - (a.breakHrs || 0));
    }

    return result;
  }, [dateAndDeptRecords, statusFilter, searchEmployee]);

  // Click handler for top stat cards: filters table to related data
  const handleCardClick = (cardKey) => {
    if (activeCardFilter === cardKey) {
      // Toggle off -> reset to ALL
      setActiveCardFilter("ALL");
      setStatusFilter("ALL");
    } else {
      setActiveCardFilter(cardKey);
      if (cardKey === "PRESENT") setStatusFilter("Present");
      else if (cardKey === "WORKING") setStatusFilter("Working");
      else if (cardKey === "ON_BREAK") setStatusFilter("On Break");
      else if (cardKey === "INACTIVE") setStatusFilter("Screen Inactive");
      else if (cardKey === "ACTIVE_WORK") setStatusFilter("Active Work");
      else if (cardKey === "BREAK_TIME") setStatusFilter("Break Time");
      else setStatusFilter("ALL");
    }
    // Smooth scroll down to table section
    if (tableSectionRef.current) {
      tableSectionRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  // Sync toolbar dropdown with active card selection
  const handleStatusFilterChange = (val) => {
    setStatusFilter(val);
    if (val === "Present") setActiveCardFilter("PRESENT");
    else if (val === "Working") setActiveCardFilter("WORKING");
    else if (val === "On Break") setActiveCardFilter("ON_BREAK");
    else if (val === "Screen Inactive") setActiveCardFilter("INACTIVE");
    else if (val === "Active Work") setActiveCardFilter("ACTIVE_WORK");
    else if (val === "Break Time") setActiveCardFilter("BREAK_TIME");
    else setActiveCardFilter("ALL");
  };

  // Pagination State (matching InstallationList.js)
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Auto-reset to first page when any search/filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [dateFilter, customDate, selectedDept, statusFilter, searchEmployee]);

  const totalRecords = filteredRecords.length;
  const totalPages = Math.ceil(totalRecords / pageSize) || 1;
  const startIndex = totalRecords === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const endIndex = Math.min(currentPage * pageSize, totalRecords);

  const paginatedRecords = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredRecords.slice(start, start + pageSize);
  }, [filteredRecords, currentPage, pageSize]);

  // Pagination Item Generator with smart range and ellipsis
  const renderPaginationItems = () => {
    if (totalPages <= 1) return null;
    const items = [];
    let startPage = Math.max(1, currentPage - 2);
    let endPage = Math.min(totalPages, currentPage + 2);

    if (currentPage <= 3) {
      endPage = Math.min(5, totalPages);
    }
    if (currentPage >= totalPages - 2) {
      startPage = Math.max(1, totalPages - 4);
    }

    if (startPage > 1) {
      items.push(
        <Pagination.Item key={1} active={1 === currentPage} onClick={() => setCurrentPage(1)}>
          1
        </Pagination.Item>
      );
      if (startPage > 2) {
        items.push(<Pagination.Ellipsis key="ellipsis-start" disabled />);
      }
    }

    for (let p = startPage; p <= endPage; p++) {
      items.push(
        <Pagination.Item
          key={p}
          active={p === currentPage}
          onClick={() => setCurrentPage(p)}
        >
          {p}
        </Pagination.Item>
      );
    }

    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        items.push(<Pagination.Ellipsis key="ellipsis-end" disabled />);
      }
      items.push(
        <Pagination.Item
          key={totalPages}
          active={totalPages === currentPage}
          onClick={() => setCurrentPage(totalPages)}
        >
          {totalPages}
        </Pagination.Item>
      );
    }

    return items;
  };

  // Export Date-Wise Data to CSV
  const handleExportDateWiseCSV = () => {
    if (!filteredRecords || filteredRecords.length === 0) {
      alert("No data available to download for the selected filter.");
      return;
    }

    const headers = [
      "Date",
      "Employee ID",
      "Employee Name",
      "Department",
      "Role",
      "Work Status",
      "Break Status / Type",
      "Login Time",
      "Logout Time",
      // "Screen Lock Time",
      // "Screen Lock Seconds",
      "Idle Time",
      "Idle Seconds",
      "Break Duration",
      "Break Seconds",
      "Breaks Taken Count",
      "Break Details",
      "Active Work Time",
      "Active Seconds",
      "Total Shift Duration",
      "Total Shift Seconds",
      "Productivity Score (%)",
    ];

    const rows = filteredRecords.map((r) => {
      const breakDetails = (r.breaks || [])
        .map((b) => `${b.type} (${b.durationFormatted})`)
        .join("; ");

      return [
        `"${r.date}"`,
        `"${r.id}"`,
        `"${r.name}"`,
        `"${r.dept}"`,
        `"${r.role}"`,
        `"${r.status}"`,
        `"${r.currentBreakType || "None"}"`,
        `"${r.loginTime || "--"}"`,
        `"${r.logoutTime || "--"}"`,
        // `"${formatDuration(r.screenLockHrs || 0)}"`,
        // r.screenLockHrs || 0,
        `"${formatDuration(r.idleHrs || 0)}"`,
        r.idleHrs || 0,
        `"${formatBreakTimeMinutes(r.breakHrs || 0)}"`,
        r.breakHrs || 0,
        r.breaks ? r.breaks.length : 0,
        `"${breakDetails}"`,
        `"${formatDuration(r.activeHrs || 0)}"`,
        r.activeHrs || 0,
        `"${formatDuration(r.totalShiftHrs || 0)}"`,
        r.totalShiftHrs || 0,
        `"${r.score}%"`,
      ];
    });

    const csvContent = [headers.join(","), ...rows.map((e) => e.join(","))].join("\r\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    const labelDate =
      dateFilter === "TODAY"
        ? todayDisplay
        : dateFilter === "YESTERDAY"
        ? yesterdayDisplay
        : dateFilter === "LAST_7_DAYS"
        ? "Last_7_Days"
        : dateFilter === "LAST_30_DAYS"
        ? "Last_30_Days"
        : dateFilter === "THIS_MONTH"
        ? "This_Month"
        : dateFilter === "CUSTOM"
        ? formatIsoToDisplay(customDate)
        : dateFilter === "CUSTOM_RANGE"
        ? `${startDate}_to_${endDate}`
        : "All_Dates";

    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `Petropulse_DateWise_Workforce_Report_${labelDate.replace(/[\s,]+/g, "_")}.csv`
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Export Individual Employee Date-Wise Report to CSV
  const handleExportSingleEmployeeCSV = (staff) => {
    if (!staff) return;

    const breakRows = (staff.breaks || []).map((b) => [
      `"Break"`,
      `"${b.type}"`,
      `"${b.reason || b.type}"`,
      `"${b.startTime}"`,
      `"${b.endTime}"`,
      `"${b.durationFormatted}"`,
      `"${b.status}"`,
    ]);

    const contentLines = [
      `"PETROPULSE EMPLOYEE DAILY WORK & BREAK AUDIT REPORT"`,
      `"Employee ID","${staff.id}"`,
      `"Employee Name","${staff.name}"`,
      `"Department","${staff.dept}"`,
      `"Role","${staff.role}"`,
      `"Date","${staff.date}"`,
      `"Work Status","${staff.status}"`,
      `"Login Time","${staff.loginTime || "--"}"`,
      `"Logout Time","${staff.hasLogout ? staff.logoutTime : "-- (Active)"}"`,
      // `"Screen Lock Time","${formatDuration(staff.screenLockHrs || 0)} (${formatDurationHuman(staff.screenLockHrs || 0)})"`,
      `"Idle Time","${formatDuration(staff.idleHrs || 0)} (${formatDurationHuman(staff.idleHrs || 0)})"`,
      `"Total Break Time","${formatDuration(staff.breakHrs)} (${formatDurationHuman(staff.breakHrs)})"`,
      `"Active Working Time","${formatDuration(staff.activeHrs)} (${formatDurationHuman(staff.activeHrs)})"`,
      `"Total Shift Duration","${formatDuration(staff.totalShiftHrs)} (${formatDurationHuman(staff.totalShiftHrs)})"`,
      `"Productivity Compliance Score","${staff.score}%"`,
      ``,
      `"BREAK AUDIT LOG FOR ${staff.date.toUpperCase()}"`,
      `"Category","Break Type","Reason","Start Time","End Time","Duration","Status"`,
      ...breakRows.map((r) => r.join(",")),
    ];

    const csvContent = contentLines.join("\r\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `Petropulse_Daily_Report_${staff.id}_${staff.date.replace(/[\s,]+/g, "_")}.csv`
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Render Status Badge for Table: Active, Not Started Yet, Shift Completed
  const renderWorkStatusBadge = (staff) => {
    if (staff.status === "Active" || staff.status === "Working") {
      return (
        <span className="badge-working">
          <span className="pulsing-dot" />
          Active
        </span>
      );
    }
    if (staff.status === "Shift Completed" || staff.status === "Completed") {
      return (
        <span className="badge-completed">
          <FontAwesomeIcon icon={faCheckCircle} className="me-1 text-success" />
          Shift Completed
        </span>
      );
    }
    if (staff.status === "Not Started Yet" || staff.status === "Not Started") {
      return (
        <span
          className="badge-notstarted"
          style={{
            backgroundColor: "#f1f5f9",
            color: "#475569",
            border: "1px solid #cbd5e1",
            padding: "4px 10px",
            borderRadius: "20px",
            fontSize: "0.78rem",
            fontWeight: "600",
            display: "inline-flex",
            alignItems: "center",
          }}
        >
          <FontAwesomeIcon icon={faClock} className="me-1 text-secondary" />
          Not Started Yet
        </span>
      );
    }
    if (staff.status === "Leave") {
      return <Badge bg="light" text="dark" className="border">On Leave</Badge>;
    }
    return <Badge bg="primary">{staff.status}</Badge>;
  };

  return (
    <TaskManagementProvider>
      <div className="activity-tracker-container">
        <Container fluid className="px-lg-3 py-2">
          <div className="manager-analytics-screen">
            {/* HEADER BANNER */}
            <div className="tracker-manager-header d-flex flex-wrap justify-content-between align-items-center mb-3">
              <div>
                <h4 className="text-white mb-1 d-flex align-items-center gap-2">
                  <FontAwesomeIcon icon={managerScreenView === "TASKS" ? faTasks : faChartPie} className="text-warning" />
                  <span>{managerScreenView === "TASKS" ? "Manager Task & Project Hub" : "Manager Workforce Dashboard"}</span>
                </h4>
                <p className="text-muted mb-0 fs-7">
                  {managerScreenView === "TASKS"
                    ? "Manage team projects, assign deliverables, review submissions, and track operational progress"
                    : "Real-time workforce shift tracking, activity logs, break audits, and productivity compliance"}
                </p>
              </div>

              {/* VIEW SWITCHER */}
              <div className="manager-view-switcher mt-3 mt-lg-0">
                <Button
                  variant={managerScreenView === "ATTENDANCE" ? "light" : "outline-light"}
                  size="sm"
                  className="d-flex align-items-center gap-2"
                  onClick={() => setManagerScreenView("ATTENDANCE")}
                >
                  <FontAwesomeIcon icon={faUsers} />
                  <span>Workforce Attendance</span>
                </Button>
                <Button
                  variant={managerScreenView === "TASKS" ? "light" : "outline-light"}
                  size="sm"
                  className="d-flex align-items-center gap-2"
                  onClick={() => setManagerScreenView("TASKS")}
                >
                  <FontAwesomeIcon icon={faTasks} />
                  <span>Tasks &amp; Projects</span>
                </Button>
              </div>
            </div>

            {/* CONDITIONAL CONTENT: 1) TASKS & PROJECT HUB OR 2) WORKFORCE ATTENDANCE */}
            {managerScreenView === "TASKS" ? (
              <ManagerTaskManagement />
            ) : (
              <>
                {/* DYNAMIC TOP STATS ROW */}
          <Row className="g-3 mb-4">
            {/* 1. Workforce Present */}
            <Col xs={6} sm={6} lg={4} xl={2}>
              <Card
                className={`tracker-stat-card card-session card-interactive h-100 ${
                  activeCardFilter === "PRESENT" ? "is-selected selected-session" : ""
                }`}
                onClick={() => handleCardClick("PRESENT")}
                role="button"
                title="Click to view all present workforce"
              >
                <Card.Body>
                  <div className="tracker-stat-header">
                    <span className="tracker-stat-label">Workforce Present</span>
                    <div className="tracker-stat-icon-wrap stat-icon-session">
                      <FontAwesomeIcon icon={faUsers} />
                    </div>
                  </div>
                  <div className="tracker-stat-value-wrap">
                    <div className="tracker-stat-value text-session">
                      {summary.presentCount} <span className="tracker-stat-denom">/ {summary.totalCount}</span>
                    </div>
                  </div>
                  <div className="tracker-stat-footer">
                    <span className="tracker-stat-dot dot-session" />
                    <span className="tracker-stat-subtext">
                      {activeCardFilter === "PRESENT" ? "Filtered • Click to reset" : "Active Today"}
                    </span>
                  </div>
                </Card.Body>
              </Card>
            </Col>

            {/* 2. Currently Working */}
            <Col xs={6} sm={6} lg={4} xl={2}>
              <Card
                className={`tracker-stat-card card-active card-interactive h-100 ${
                  activeCardFilter === "WORKING" ? "is-selected selected-active" : ""
                }`}
                onClick={() => handleCardClick("WORKING")}
                role="button"
                title="Click to view employees currently working"
              >
                <Card.Body>
                  <div className="tracker-stat-header">
                    <span className="tracker-stat-label">Currently Working</span>
                    <div className="tracker-stat-icon-wrap stat-icon-active">
                      <FontAwesomeIcon icon={faUserClock} />
                    </div>
                  </div>
                  <div className="tracker-stat-value-wrap">
                    <div className="tracker-stat-value text-active">
                      {summary.workingCount}
                    </div>
                  </div>
                  <div className="tracker-stat-footer">
                    <span className="tracker-stat-dot dot-active" />
                    <span className="tracker-stat-subtext">
                      {activeCardFilter === "WORKING" ? "Filtered • Click to reset" : "On active screens"}
                    </span>
                  </div>
                </Card.Body>
              </Card>
            </Col>

            {/* 3. On Break Now */}
            <Col xs={6} sm={6} lg={4} xl={2}>
              <Card
                className={`tracker-stat-card card-break card-interactive h-100 ${
                  activeCardFilter === "ON_BREAK" ? "is-selected selected-break" : ""
                }`}
                onClick={() => handleCardClick("ON_BREAK")}
                role="button"
                title="Click to view employees currently on break"
              >
                <Card.Body>
                  <div className="tracker-stat-header">
                    <span className="tracker-stat-label">On Break Now</span>
                    <div className="tracker-stat-icon-wrap stat-icon-break">
                      <FontAwesomeIcon icon={faCoffee} />
                    </div>
                  </div>
                  <div className="tracker-stat-value-wrap">
                    <div className="tracker-stat-value text-break">
                      {summary.breakCount}
                    </div>
                  </div>
                  <div className="tracker-stat-footer">
                    <span className="tracker-stat-dot dot-break" />
                    <span className="tracker-stat-subtext">
                      {activeCardFilter === "ON_BREAK" ? "Filtered • Click to reset" : "Tea • Lunch breaks"}
                    </span>
                  </div>
                </Card.Body>
              </Card>
            </Col>

            {/* 4. Screen Inactive */}
            <Col xs={6} sm={6} lg={4} xl={2}>
              <Card
                className={`tracker-stat-card card-inactive card-interactive h-100 ${
                  activeCardFilter === "INACTIVE" ? "is-selected selected-inactive" : ""
                }`}
                onClick={() => handleCardClick("INACTIVE")}
                role="button"
                title="Click to view screen inactive / idle employees"
              >
                <Card.Body>
                  <div className="tracker-stat-header">
                    <span className="tracker-stat-label">Screen Inactive</span>
                    <div className="tracker-stat-icon-wrap stat-icon-inactive">
                      <FontAwesomeIcon icon={faExclamationTriangle} />
                    </div>
                  </div>
                  <div className="tracker-stat-value-wrap">
                    <div className="tracker-stat-value text-inactive">
                      {summary.inactiveCount}
                    </div>
                  </div>
                  <div className="tracker-stat-footer">
                    <span className="tracker-stat-dot dot-inactive" />
                    <span className="tracker-stat-subtext">
                      {activeCardFilter === "INACTIVE" ? "Filtered • Click to reset" : "Idle / Locked"}
                    </span>
                  </div>
                </Card.Body>
              </Card>
            </Col>

            {/* 5. Avg Active Work */}
            <Col xs={6} sm={6} lg={4} xl={2}>
              <Card
                className={`tracker-stat-card card-teal card-interactive h-100 ${
                  activeCardFilter === "ACTIVE_WORK" ? "is-selected selected-teal" : ""
                }`}
                onClick={() => handleCardClick("ACTIVE_WORK")}
                role="button"
                title="Click to sort & view employees by active work time"
              >
                <Card.Body>
                  <div className="tracker-stat-header">
                    <span className="tracker-stat-label">Avg Active Work</span>
                    <div className="tracker-stat-icon-wrap stat-icon-teal">
                      <FontAwesomeIcon icon={faClock} />
                    </div>
                  </div>
                  <div className="tracker-stat-value-wrap">
                    <div className="tracker-stat-value text-teal">
                      {summary.avgActiveStr}
                    </div>
                  </div>
                  <div className="tracker-stat-footer">
                    <span className="tracker-stat-dot dot-teal" />
                    <span className="tracker-stat-subtext">
                      {activeCardFilter === "ACTIVE_WORK" ? "Ranked • Click to reset" : "Per employee"}
                    </span>
                  </div>
                </Card.Body>
              </Card>
            </Col>

            {/* 6. Avg Break Time */}
            <Col xs={6} sm={6} lg={4} xl={2}>
              <Card
                className={`tracker-stat-card card-purple card-interactive h-100 ${
                  activeCardFilter === "BREAK_TIME" ? "is-selected selected-purple" : ""
                }`}
                onClick={() => handleCardClick("BREAK_TIME")}
                role="button"
                title="Click to sort & view employees who took breaks"
              >
                <Card.Body>
                  <div className="tracker-stat-header">
                    <span className="tracker-stat-label">Avg Break Time</span>
                    <div className="tracker-stat-icon-wrap stat-icon-purple">
                      <FontAwesomeIcon icon={faShieldAlt} />
                    </div>
                  </div>
                  <div className="tracker-stat-value-wrap">
                    <div className="tracker-stat-value text-purple">
                      {summary.avgBreakStr}
                    </div>
                  </div>
                  <div className="tracker-stat-footer">
                    <span className="tracker-stat-dot dot-purple" />
                    <span className="tracker-stat-subtext">
                      {activeCardFilter === "BREAK_TIME" ? "Ranked • Click to reset" : "Within shift standard"}
                    </span>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* DATE-WISE & DURATION FILTER TOOLBAR */}
          <div className="manager-filter-toolbar mb-4">
            <Row className="g-3 align-items-end">
              {/* Date & Duration Selector */}
              <Col xs={12} sm={6} md={dateFilter === "CUSTOM" || dateFilter === "CUSTOM_RANGE" ? 3 : 3}>
                <Form.Label className="small fw-bold text-dark mb-1 d-flex align-items-center gap-1">
                  <FontAwesomeIcon icon={faCalendarAlt} className="text-primary" />
                  <span>Date / Duration:</span>
                </Form.Label>
                <Form.Select
                  size="sm"
                  className="fw-semibold"
                  value={dateFilter}
                  onChange={(e) => setDateFilter(e.target.value)}
                >
                  <option value="TODAY">Today's Shift ({todayDisplay})</option>
                  <option value="YESTERDAY">Yesterday ({yesterdayDisplay})</option>
                  <option value="LAST_7_DAYS">Last 7 Days (Duration)</option>
                  <option value="LAST_30_DAYS">Last 30 Days (Duration)</option>
                  <option value="THIS_MONTH">This Month (Duration)</option>
                  <option value="CUSTOM">Specific Single Date...</option>
                  <option value="CUSTOM_RANGE">Custom Duration (From - To)...</option>
                  <option value="ALL">All Available Dates</option>
                </Form.Select>
              </Col>

              {/* Single Date Picker */}
              {dateFilter === "CUSTOM" && (
                <Col xs={12} sm={6} md={2}>
                  <Form.Label className="small fw-bold text-dark mb-1">Select Date:</Form.Label>
                  <Form.Control
                    type="date"
                    size="sm"
                    value={customDate}
                    onChange={(e) => setCustomDate(e.target.value)}
                  />
                </Col>
              )}

              {/* Custom Duration (From - To) Pickers */}
              {dateFilter === "CUSTOM_RANGE" && (
                <>
                  <Col xs={12} sm={6} md={2}>
                    <Form.Label className="small fw-bold text-dark mb-1">From Date:</Form.Label>
                    <Form.Control
                      type="date"
                      size="sm"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                    />
                  </Col>
                  <Col xs={12} sm={6} md={2}>
                    <Form.Label className="small fw-bold text-dark mb-1">To Date:</Form.Label>
                    <Form.Control
                      type="date"
                      size="sm"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                    />
                  </Col>
                </>
              )}

              {/* Work & Break Status Filter */}
              <Col xs={12} sm={6} md={dateFilter === "CUSTOM_RANGE" ? 2 : (dateFilter === "CUSTOM" ? 2 : 3)}>
                <Form.Label className="small fw-bold text-dark mb-1 d-flex align-items-center gap-1">
                  <FontAwesomeIcon icon={faFilter} className="text-secondary" />
                  <span>Work &amp; Break Status:</span>
                </Form.Label>
                <Form.Select
                  size="sm"
                  value={statusFilter}
                  onChange={(e) => handleStatusFilterChange(e.target.value)}
                >
                  <option value="ALL">All Statuses</option>
                  <option value="Present">Workforce Present</option>
                  <option value="Working">Working / Active</option>
                  <option value="On Break">On Break (Tea / Lunch)</option>
                  <option value="Screen Inactive">Screen Inactive / Idle</option>
                  <option value="Active Work">Employees with Active Work</option>
                  <option value="Break Time">Employees with Breaks</option>
                  <option value="Completed">Shift Completed</option>
                  <option value="Leave">On Leave</option>
                </Form.Select>
              </Col>

              {/* Department Filter */}
              <Col xs={12} sm={6} md={dateFilter === "CUSTOM_RANGE" ? 2 : (dateFilter === "CUSTOM" ? 2 : 3)}>
                <Form.Label className="small fw-bold text-dark mb-1 d-flex align-items-center gap-1">
                  <FontAwesomeIcon icon={faBuilding} className="text-secondary" />
                  <span>Department:</span>
                </Form.Label>
                <Form.Select
                  size="sm"
                  value={selectedDept}
                  onChange={(e) => setSelectedDept(e.target.value)}
                >
                  <option value="ALL">All Departments</option>
                  {availableDepartments.map((dept) => (
                    <option key={dept} value={dept}>{dept}</option>
                  ))}
                </Form.Select>
              </Col>

              {/* Search Field */}
              <Col xs={12} sm={6} md={dateFilter === "CUSTOM_RANGE" ? 3 : (dateFilter === "CUSTOM" ? 3 : 3)}>
                <Form.Label className="small fw-bold text-dark mb-1 d-flex align-items-center gap-1">
                  <FontAwesomeIcon icon={faSearch} className="text-secondary" />
                  <span>Search Staff:</span>
                </Form.Label>
                <InputGroup size="sm">
                  <Form.Control
                    placeholder="Name, ID or Role..."
                    value={searchEmployee}
                    onChange={(e) => setSearchEmployee(e.target.value)}
                  />
                  {searchEmployee && (
                    <Button variant="outline-secondary" onClick={() => setSearchEmployee("")}>
                      &times;
                    </Button>
                  )}
                </InputGroup>
              </Col>
            </Row>

            {/* Quick summary strip inside toolbar */}
            <div className="d-flex flex-wrap align-items-center justify-content-between pt-2 mt-3 border-top gap-2">
              <div className="d-flex align-items-center gap-2">
                <Badge bg="primary" className="fw-semibold px-2 py-1">
                  <FontAwesomeIcon icon={faCalendarAlt} className="me-1" />
                  {durationLabel}
                </Badge>
                <span className="small text-muted">
                  Showing <strong>{filteredRecords.length}</strong> records across workforce
                </span>
              </div>
              <Button
                variant="outline-success"
                size="sm"
                className="d-flex align-items-center gap-1 fw-bold"
                onClick={handleExportDateWiseCSV}
              >
                <FontAwesomeIcon icon={faDownload} />
                <span>Export Report CSV</span>
              </Button>
            </div>
          </div>

          {/* MAIN EMPLOYEE WORK & BREAK STATUS TABLE */}
          <Card className="border-0 shadow-sm mb-4" ref={tableSectionRef}>
            <Card.Header className="bg-white border-bottom py-3 d-flex flex-wrap justify-content-between align-items-center gap-2">
              <div>
                <h5 className="mb-0 fw-bold text-dark d-flex align-items-center gap-2">
                  <FontAwesomeIcon icon={faUsers} className="text-primary" />
                  <span>Employee Work &amp; Break Status Directory</span>
                </h5>
                <div className="d-flex align-items-center gap-2 mt-1">
                  <small className="text-muted">
                    Showing <strong>{filteredRecords.length}</strong> records &bull; Shifts start at 09:00 AM Standard
                  </small>
                  {lastSyncTime && (
                    <small className="text-muted border-start ps-2">
                      Last synced: {formatTimeStr(lastSyncTime.getTime())}
                    </small>
                  )}
                  {activeCardFilter !== "ALL" && (
                    <Badge
                      bg="primary"
                      className="d-inline-flex align-items-center gap-1 py-1 px-2 text-white"
                      style={{ cursor: "pointer" }}
                      onClick={() => handleCardClick(activeCardFilter)}
                      title="Click to reset filter and show all"
                    >
                      <span>
                        Filtered:{" "}
                        {activeCardFilter === "PRESENT"
                          ? "Workforce Present"
                          : activeCardFilter === "WORKING"
                          ? "Currently Working"
                          : activeCardFilter === "ON_BREAK"
                          ? "On Break Now"
                          : activeCardFilter === "INACTIVE"
                          ? "Screen Inactive"
                          : activeCardFilter === "ACTIVE_WORK"
                          ? "Active Work"
                          : activeCardFilter === "BREAK_TIME"
                          ? "Break Time"
                          : "Filtered"}
                      </span>
                      <FontAwesomeIcon icon={faTimes} className="ms-1" />
                    </Badge>
                  )}
                </div>
              </div>

              <div className="d-flex align-items-center gap-2">
                <Button
                  variant="outline-primary"
                  size="sm"
                  className="fw-bold d-flex align-items-center gap-2"
                  onClick={fetchDeskDetails}
                  disabled={isServerLoading}
                  title="Fetch latest records from server"
                >
                  <FontAwesomeIcon icon={faSync} spin={isServerLoading} />
                  <span>{isServerLoading ? "Syncing..." : "Sync Data"}</span>
                </Button>
                <Button
                  variant="outline-success"
                  size="sm"
                  className="fw-bold d-flex align-items-center gap-2"
                  onClick={handleExportDateWiseCSV}
                >
                  <FontAwesomeIcon icon={faDownload} />
                  <span>Download Filtered Records (CSV)</span>
                </Button>
              </div>
            </Card.Header>

            <Card.Body className="p-0">
              <div className="tracker-table-vertical-wrap">
                <div className="mobile-table-scroll-hint">
                  <span>Swipe horizontally to view all columns &rarr;</span>
                </div>
                <Table hover className="tracker-table align-middle mb-0">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Employee</th>
                      <th>Department &amp; Role</th>
                      <th>Work Status</th>
                      <th>Login Time</th>
                      <th>Logout Time</th>
                      {/* <th>Screen Lock Time</th> */}
                      <th>Idle Time</th>
                      <th>Break Time</th>
                      <th>Active Work Time</th>
                      <th>Total Shift</th>
                      <th>Compliance</th>
                      <th className="text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {isServerLoading && paginatedRecords.length === 0 ? (
                      <tr>
                        <td colSpan="12" className="text-center py-4">
                          <Spinner animation="border" size="sm" variant="primary" className="me-2" />
                          <span className="text-muted small">Loading workforce data from server...</span>
                        </td>
                      </tr>
                    ) : paginatedRecords.length === 0 ? (
                      <tr>
                        <td colSpan="12" className="text-center py-5 text-muted">
                          <div className="py-3">
                            <FontAwesomeIcon icon={faExclamationTriangle} className="text-secondary opacity-50 mb-2 fs-3" />
                            <div className="fw-bold text-dark fs-6">No data available</div>
                            <small className="text-muted">No desktop tracking records returned from server.</small>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      paginatedRecords.map((staff) => (
                        <tr key={`${staff.id}-${staff.date}-${staff.rawServerId || ""}`}>
                          {/* 1. Date */}
                          <td className="fw-semibold text-dark small">{staff.date}</td>

                          {/* 2. Employee */}
                          <td>
                            <div className="d-flex align-items-center gap-2">
                              <div
                                className="rounded-circle text-white fw-bold d-flex align-items-center justify-content-center"
                                style={{
                                  width: "32px",
                                  height: "32px",
                                  backgroundColor: staff.isDesktopServerRecord ? "#0d6efd" : "#1e293b",
                                  fontSize: "0.85rem",
                                }}
                              >
                                {staff.name.charAt(0)}
                              </div>
                              <div>
                                <div className="fw-bold text-dark">{staff.name}</div>
                                <small className="text-muted font-monospace">{staff.id}</small>
                              </div>
                            </div>
                          </td>

                          {/* 3. Department & Role */}
                          <td>
                            <div className="small fw-semibold">{staff.role}</div>
                            <small className="text-muted">{staff.dept}</small>
                          </td>

                          {/* 4. Work Status: Active, Not Started Yet, Shift Completed based on login/logout */}
                          <td>{renderWorkStatusBadge(staff)}</td>

                          {/* 5. Login Time */}
                          <td>
                            {staff.attendance === "Present" ? (
                              <span className="fw-bold text-dark font-monospace small">
                                {staff.loginTime || "--"}
                              </span>
                            ) : (
                              <span className="text-muted">--</span>
                            )}
                          </td>

                          {/* 6. Logout Time */}
                          <td>
                            {staff.attendance === "Present" ? (
                              staff.hasLogout ? (
                                <span className="fw-bold text-dark font-monospace small">
                                  {staff.logoutTime}
                                </span>
                              ) : (
                                <span className="text-primary font-monospace small fst-italic">
                                  -- (Active)
                                </span>
                              )
                            ) : (
                              <span className="text-muted">--</span>
                            )}
                          </td>

                          {/* 7. Screen Lock Time */}
                          {/* <td>
                            {staff.attendance === "Present" ? (
                              <div>
                                <span className="font-monospace text-warning fw-bold small">
                                  {formatDuration(staff.screenLockHrs || 0)}
                                </span>
                                <div className="text-muted" style={{ fontSize: "0.70rem" }}>
                                  {formatDurationHuman(staff.screenLockHrs || 0)}
                                </div>
                              </div>
                            ) : (
                              <span className="text-muted">--</span>
                            )}
                          </td> */}

                          {/* 8. Idle Time */}
                          <td>
                            {staff.attendance === "Present" ? (
                              <div>
                                <span className="font-monospace text-danger fw-bold small">
                                  {formatDuration(staff.idleHrs || 0)}
                                </span>
                                <div className="text-muted" style={{ fontSize: "0.70rem" }}>
                                  {formatDurationHuman(staff.idleHrs || 0)}
                                </div>
                              </div>
                            ) : (
                              <span className="text-muted">--</span>
                            )}
                          </td>

                          {/* 9. Break Time & Details */}
                          <td>
                            {staff.attendance === "Present" ? (
                              <div>
                                <span className="font-monospace text-primary fw-bold small">
                                  {formatBreakTimeMinutes(staff.breakHrs || 0)}
                                </span>
                                <div className="mt-1">
                                  {(staff.breaks || []).map((b) => (
                                    <span key={b.id} className="break-tag-chip" title={`${b.type}: ${formatBreakTimeMinutes(b.durationSeconds || b.durationFormatted)} (${b.reason})`}>
                                      {b.type.replace(" Break", "")} {formatBreakTimeMinutes(b.durationSeconds || b.durationFormatted)}
                                    </span>
                                  ))}
                                  {(!staff.breaks || staff.breaks.length === 0) && (!staff.breakHrs || staff.breakHrs === 0) && (
                                    <span className="text-muted" style={{ fontSize: "0.70rem" }}>No breaks</span>
                                  )}
                                </div>
                              </div>
                            ) : (
                              <span className="text-muted">--</span>
                            )}
                          </td>

                          {/* 10. Active Work Time (screenactivetime) */}
                          <td>
                            {staff.attendance === "Present" ? (
                              <div>
                                <div className="d-flex align-items-center gap-1">
                                  <span className="font-monospace text-success fw-bold small">
                                    {formatDuration(staff.activeHrs || 0)}
                                  </span>
                                  {!staff.hasLogout && (staff.status === "Active" || staff.status === "Working") && (
                                    <span
                                      className="badge bg-success text-white py-0 px-1"
                                      style={{ fontSize: "0.60rem", letterSpacing: "0.5px" }}
                                      title="Live screen active time incrementing dynamically"
                                    >
                                      LIVE
                                    </span>
                                  )}
                                </div>
                                <div className="text-muted" style={{ fontSize: "0.70rem" }}>
                                  {formatDurationHuman(staff.activeHrs || 0)}
                                </div>
                              </div>
                            ) : (
                              <span className="text-muted">--</span>
                            )}
                          </td>

                          {/* 11. Total Shift */}
                          <td>
                            {staff.attendance === "Present" ? (
                              <div>
                                <span className="font-monospace text-dark fw-bold small">
                                  {formatDuration(staff.totalShiftHrs || 0)}
                                </span>
                                <div className="text-muted" style={{ fontSize: "0.70rem" }}>
                                  {formatDurationHuman(staff.totalShiftHrs || 0)}
                                </div>
                              </div>
                            ) : (
                              <span className="text-muted">--</span>
                            )}
                          </td>

                          {/* 12. Compliance / Score */}
                          <td>
                            {staff.attendance === "Present" ? (
                              <div className="d-flex align-items-center gap-2">
                                <span className="small fw-bold">{staff.score}%</span>
                                <ProgressBar
                                  now={staff.score}
                                  variant={staff.score >= 90 ? "success" : staff.score >= 80 ? "primary" : "warning"}
                                  style={{ height: 5, width: 45 }}
                                />
                              </div>
                            ) : (
                              <span className="text-muted">--</span>
                            )}
                          </td>

                          {/* 13. Actions */}
                          <td className="text-center">
                            <div className="d-flex align-items-center justify-content-center gap-1">
                              <Button
                                variant="outline-primary"
                                size="sm"
                                className="py-1 px-2"
                                title="View detailed work & break breakdown"
                                onClick={() => {
                                  setSelectedStaff(staff);
                                  setShowStaffModal(true);
                                }}
                              >
                                <FontAwesomeIcon icon={faEye} />
                              </Button>
                              <Button
                                variant="outline-success"
                                size="sm"
                                className="py-1 px-2"
                                title="Download employee report (CSV)"
                                onClick={() => handleExportSingleEmployeeCSV(staff)}
                              >
                                <FontAwesomeIcon icon={faDownload} />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>
              </div>
            </Card.Body>

            <Card.Footer className="tracker-pagination-footer">
              <Row className="align-items-center g-2 tracker-pagination-row">
                <Col xs={12} lg={6} className="d-flex flex-wrap align-items-center justify-content-center justify-content-lg-start gap-2 tracker-pagination-info">
                  <span className="small text-muted fw-bold">Show</span>
                  <Form.Select
                    size="sm"
                    style={{ width: "75px" }}
                    value={pageSize}
                    onChange={(e) => {
                      setPageSize(Number(e.target.value));
                      setCurrentPage(1);
                    }}
                    className="fw-semibold"
                  >
                    <option value={5}>5</option>
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                  </Form.Select>
                  <span className="small text-muted fw-bold">Entries per page</span>
                  <span className="small text-muted ms-md-3">
                    Showing <strong>{startIndex}</strong> to <strong>{endIndex}</strong> of{" "}
                    <strong>{totalRecords}</strong> entries
                  </span>
                </Col>
                <Col xs={12} lg={6} className="d-flex justify-content-center justify-content-lg-end align-items-center mt-2 mt-lg-0">
                  <Pagination size="sm" className="mb-0 flex-wrap justify-content-center">
                    <Pagination.First
                      disabled={currentPage === 1}
                      onClick={() => setCurrentPage(1)}
                    />
                    <Pagination.Prev
                      disabled={currentPage === 1}
                      onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    />
                    {renderPaginationItems()}
                    <Pagination.Next
                      disabled={currentPage === totalPages || totalPages === 0}
                      onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                    />
                    <Pagination.Last
                      disabled={currentPage === totalPages || totalPages === 0}
                      onClick={() => setCurrentPage(totalPages)}
                    />
                  </Pagination>
                </Col>
              </Row>
            </Card.Footer>
          </Card>

          {/* DRILL DOWN EMPLOYEE DAILY WORK & BREAK DETAILS MODAL */}
          <Modal show={showStaffModal} onHide={() => setShowStaffModal(false)} centered size="lg">
            <Modal.Header closeButton className="tracker-modal-header text-white">
              <Modal.Title className="fs-5 fw-bold d-flex align-items-center gap-2">
                <FontAwesomeIcon icon={faUserClock} className="text-info" />
                <span>Employee Daily Work &amp; Break Breakdown</span>
              </Modal.Title>
            </Modal.Header>
            <Modal.Body className="p-4">
              {selectedStaff && (
                <div>
                  {/* Employee & Date Banner */}
                  <div className="p-3 bg-light rounded border mb-3">
                    <Row className="align-items-center g-2">
                      <Col xs={12} sm={8}>
                        <h5 className="mb-0 text-dark fw-bold">{selectedStaff.name}</h5>
                        <div className="small text-muted mt-1">
                          <strong>ID:</strong> {selectedStaff.id} &bull;{" "}
                          <strong>Dept:</strong> {selectedStaff.dept} &bull;{" "}
                          <strong>Role:</strong> {selectedStaff.role}
                        </div>
                      </Col>
                      <Col xs={12} sm={4} className="text-sm-end">
                        <div className="mb-1">{renderWorkStatusBadge(selectedStaff)}</div>
                        <span className="badge bg-primary bg-opacity-15 text-white small">
                          Date: {selectedStaff.date}
                        </span>
                      </Col>
                    </Row>
                  </div>

                  {/* Shift Timing Banner */}
                  <div className="p-2 px-3 rounded bg-white border d-flex flex-wrap justify-content-between align-items-center mb-3 small gap-2">
                    <div>
                      <span className="text-muted">Login Time:</span>{" "}
                      <strong className="text-dark font-monospace">{selectedStaff.loginTime || "--"}</strong>
                    </div>
                    <div>
                      <span className="text-muted">Logout Time:</span>{" "}
                      <strong className="text-dark font-monospace">
                        {selectedStaff.hasLogout ? selectedStaff.logoutTime : "-- (Active)"}
                      </strong>
                    </div>
                    <div>
                      <span className="text-muted">Total Shift Duration:</span>{" "}
                      <strong className="text-primary font-monospace">{formatDuration(selectedStaff.totalShiftHrs)}</strong>
                      <span className="text-muted ms-1">({formatDurationHuman(selectedStaff.totalShiftHrs)})</span>
                    </div>
                  </div>

                  {/* Live Tracking Details Banner */}
                  {selectedStaff.isDesktopServerRecord && (
                    <div className="p-2 px-3 rounded bg-light border d-flex flex-wrap justify-content-between align-items-center mb-3 small gap-2">
                      <div>
                        <span className="text-muted fw-bold">Username:</span>{" "}
                        <strong className="text-primary font-monospace">{selectedStaff.deskusername}</strong>
                      </div>
                      <div>
                        <span className="text-muted fw-bold">Active Work Time:</span>{" "}
                        <span className="text-success fw-bold font-monospace">{formatDuration((activeStaffDetail || selectedStaff).activeHrs || 0)}</span>
                      </div>
                      {/* <div>
                        <span className="text-muted fw-bold">Screen Lock:</span>{" "}
                        <span className="text-warning fw-bold font-monospace">{formatDuration((activeStaffDetail || selectedStaff).screenLockHrs || 0)}</span>
                      </div> */}
                      <div>
                        <span className="text-muted fw-bold">Idle Time:</span>{" "}
                        <span className="text-danger fw-bold font-monospace">{formatDuration((activeStaffDetail || selectedStaff).idleHrs || 0)}</span>
                      </div>
                    </div>
                  )}

                  {/* Metric Cards */}
                  <Row className="g-2 text-center mb-3">
                    <Col xs={12} sm={4}>
                      <div className="p-3 border rounded bg-success bg-opacity-10 h-100">
                        <div className="small text-white fw-bold">Active Work</div>
                        <div className="fw-bold text-success fs-5">{formatDuration((activeStaffDetail || selectedStaff).activeHrs || 0)}</div>
                        <small className="text-white">{formatDurationHuman((activeStaffDetail || selectedStaff).activeHrs || 0)}</small>
                      </div>
                    </Col>
                    {/* <Col xs={6} sm={3}>
                      <div className="p-3 border rounded bg-warning bg-opacity-10 h-100">
                        <div className="small text-white fw-bold">Screen Lock</div>
                        <div className="fw-bold text-warning fs-5">{formatDuration(selectedStaff.screenLockHrs || 0)}</div>
                        <small className="text-white">{formatDurationHuman(selectedStaff.screenLockHrs || 0)}</small>
                      </div>
                    </Col> */}
                    <Col xs={12} sm={4}>
                      <div className="p-3 border rounded bg-danger bg-opacity-10 h-100">
                        <div className="small text-white fw-bold">Idle Time</div>
                        <div className="fw-bold text-danger fs-5">{formatDuration(selectedStaff.idleHrs || 0)}</div>
                        <small className="text-white">{formatDurationHuman(selectedStaff.idleHrs || 0)}</small>
                      </div>
                    </Col>
                    <Col xs={12} sm={4}>
                      <div className="p-3 border rounded bg-primary bg-opacity-10 h-100">
                        <div className="small text-white fw-bold">Total Break</div>
                        <div className="fw-bold text-primary fs-5">{formatBreakTimeMinutes(selectedStaff.breakHrs || 0)}</div>
                        <small className="text-white">{formatBreakTimeMinutes(selectedStaff.breakHrs || 0)}</small>
                      </div>
                    </Col>
                  </Row>

                  {/* Shift Distribution Progress Bar */}
                  {selectedStaff.totalShiftHrs > 0 && (
                    <div className="mb-4">
                      <div className="d-flex justify-content-between small text-white mb-1">
                        <span>Work ({Math.round((selectedStaff.activeHrs / selectedStaff.totalShiftHrs) * 100)}%)</span>
                        <span>Breaks ({Math.round((selectedStaff.breakHrs / selectedStaff.totalShiftHrs) * 100)}%)</span>
                        <span>Inactive ({Math.round((selectedStaff.inactiveHrs / selectedStaff.totalShiftHrs) * 100)}%)</span>
                      </div>
                      <ProgressBar style={{ height: 8 }}>
                        <ProgressBar
                          now={Math.round((selectedStaff.activeHrs / selectedStaff.totalShiftHrs) * 100)}
                          variant="success"
                          key={1}
                        />
                        <ProgressBar
                          now={Math.round((selectedStaff.breakHrs / selectedStaff.totalShiftHrs) * 100)}
                          variant="primary"
                          key={2}
                        />
                        <ProgressBar
                          now={Math.round((selectedStaff.inactiveHrs / selectedStaff.totalShiftHrs) * 100)}
                          variant="danger"
                          key={3}
                        />
                      </ProgressBar>
                    </div>
                  )}

                  {/* Detailed Break Audit Table for that Date */}
                  <h6 className="fw-bold text-dark d-flex align-items-center gap-2 mb-2">
                    <FontAwesomeIcon icon={faCoffee} className="text-primary" />
                    <span>Breaks Taken on {selectedStaff.date}</span>
                  </h6>

                  {selectedStaff.breaks && selectedStaff.breaks.length > 0 ? (
                    <div className="table-responsive border rounded mb-3">
                      <Table size="sm" hover className="tracker-table mb-0 align-middle">
                        <thead>
                          <tr>
                            <th>Break Type</th>
                            <th>Reason / Note</th>
                            <th>Start Time</th>
                            <th>End Time</th>
                            <th>Duration</th>
                            <th>Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {selectedStaff.breaks.map((b) => (
                            <tr key={b.id}>
                              <td className="fw-bold text-dark">
                                <FontAwesomeIcon
                                  icon={b.type.includes("Lunch") ? faUtensils : faCoffee}
                                  className="me-2 text-primary"
                                />
                                {b.type}
                              </td>
                              <td className="small text-muted">{b.reason || b.type}</td>
                              <td className="small">{b.startTime}</td>
                              <td className="small">{b.endTime}</td>
                              <td className="fw-bold text-primary font-monospace small">
                                {formatBreakTimeMinutes(b.durationSeconds || b.durationFormatted)}
                              </td>
                              <td>
                                <Badge bg={b.status === "In Progress" ? "warning" : "success"}>
                                  {b.status}
                                </Badge>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  ) : (
                    <p className="text-muted small fst-italic mb-3">No breaks recorded for this date.</p>
                  )}
                </div>
              )}
            </Modal.Body>
            <Modal.Footer className="bg-light d-flex justify-content-between">
              <Button
                variant="outline-success"
                size="sm"
                className="fw-bold d-flex align-items-center gap-1"
                onClick={() => handleExportSingleEmployeeCSV(selectedStaff)}
              >
                <FontAwesomeIcon icon={faDownload} />
                <span>Download Employee Day CSV</span>
              </Button>
              
            </Modal.Footer>
          </Modal>
              </>
            )}
          </div>
        </Container>
      </div>
    </TaskManagementProvider>
  );
};

export default ManagerAnalytics;
